import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  Button,
  Flex,
  Text,
  Icon,
  Box,
  Heading,
  VStack,
  Skeleton,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSession } from '@/context/SessionProvider'
import { useTRPCClient } from '@/api/trpc/context'
import { useAppToast } from '@/hooks/useAppToast'
import Image from 'next/image'
import ShuLogo from '@/public/assets/currencies/shu.png'
import { BsGiftFill } from 'react-icons/bs'

export const ShuFaucetModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation()
  const { account } = useSession()
  const trpc = useTRPCClient()
  const { onSuccess, onError } = useAppToast()
  const [claimed, setClaimed] = useState(false)

  // Check if user can claim using tRPC
  const {
    data: claimData,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ['faucet-claim-time', account?.injectiveAddress],
    queryFn: () => trpc.private.faucet.getNextClaimTime.query(),
    enabled: !!account,
    refetchInterval: 1000, // Refresh every second to update countdown
  })

  const canClaim = claimData?.canClaim ?? false
  const remainingMs = claimData?.remainingMs ?? 0

  // Claim mutation using tRPC
  const { mutate, isPending } = useMutation({
    mutationFn: () => trpc.private.faucet.claim.mutate(),
    onSuccess: async () => {
      await new Promise((resolve) => setTimeout(resolve, 2000)) // Show success message for 2 seconds
      setClaimed(true)
      await refetch() // Refresh claim status
      onSuccess({ custom: { title: t('shu-faucet.success-message') } })
    },
    onError: (error: any) => {
      const message = error?.message || error?.data?.message
      if (message?.includes('cooldown') || message?.includes('already claimed')) {
        onError({ custom: { title: t('shu-faucet.error-cooldown') } })
      } else {
        onError({ custom: { title: t('shu-faucet.error-general') } })
      }
      refetch() // Refresh to get updated cooldown
    },
  })

  const handleClaim = () => {
    mutate()
  }

  // Format time remaining as HH:MM:SS
  const formatTimeRemaining = () => {
    const hours = Math.floor(remainingMs / (60 * 60 * 1000))
    const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000))
    const seconds = Math.floor((remainingMs % (60 * 1000)) / 1000)

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  return (
    <Modal size={'sm'} isCentered={true} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg={'ninja.600'}>
        <ModalCloseButton size="sm" />
        <ModalHeader textAlign={'center'} pt={6}>
          {t('shu-faucet.title')}
        </ModalHeader>
        <ModalBody px={6} pb={6}>
          <VStack spacing={4}>
            {/* SHU Icon */}
            <Flex
              w={20}
              h={20}
              borderRadius={'full'}
              bg={'ninja.500'}
              alignItems={'center'}
              justifyContent={'center'}
              position={'relative'}
            >
              <Image src={ShuLogo} alt="SHU" width={48} height={48} />
              <Box
                position={'absolute'}
                bottom={-1}
                right={-1}
                bg={'brand.purple'}
                borderRadius={'full'}
                w={8}
                h={8}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Icon as={BsGiftFill} color={'white'} w={4} h={4} />
              </Box>
            </Flex>

            {/* Description */}
            <VStack spacing={2} textAlign={'center'}>
              <Heading fontSize={'lg'}>{t('shu-faucet.heading')}</Heading>
              <Text fontSize={'sm'} color={'ninja.200'} lineHeight={1.6}>
                {t('shu-faucet.description')}
              </Text>
            </VStack>

            {/* Success Message */}
            {claimed && (
              <Box
                w={'full'}
                bg={'rgba(76, 175, 80, 0.1)'}
                border={'1px solid'}
                borderColor={'rgba(76, 175, 80, 0.3)'}
                rounded={'lg'}
                p={3}
              >
                <Text fontSize={'sm'} color={'#4CAF50'} textAlign={'center'} fontWeight={600}>
                  {t('shu-faucet.success-message')}
                </Text>
              </Box>
            )}

            {/* Loading State */}
            {isLoading && (
              <Skeleton w={'full'} h={'70px'} rounded={'lg'} startColor={'ninja.500'} endColor={'ninja.400'} />
            )}

            {/* Cooldown Timer */}
            {!isLoading && !canClaim && (
              <Box
                w={'full'}
                bg={'rgba(255, 167, 35, 0.1)'}
                border={'1px solid'}
                borderColor={'rgba(255, 167, 35, 0.3)'}
                rounded={'lg'}
                p={3}
              >
                <VStack spacing={1}>
                  <Text fontSize={'sm'} color={'brand.yellow'} textAlign={'center'} fontWeight={600}>
                    {t('shu-faucet.next-claim-in')}
                  </Text>
                  <Text fontSize={'2xl'} color={'brand.yellow'} textAlign={'center'} fontWeight={700}>
                    {formatTimeRemaining()}
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Claim Button */}
            {isLoading ? (
              <Skeleton w={'full'} h={'52px'} rounded={'xl'} startColor={'ninja.500'} endColor={'ninja.400'} />
            ) : (
              <Button
                w={'full'}
                h={'52px'}
                rounded={'xl'}
                onClick={handleClaim}
                isLoading={isPending}
                isDisabled={!canClaim || claimed}
                variant={'orange'}
                fontWeight={700}
              >
                {claimed ? t('shu-faucet.claimed') : t('shu-faucet.claim-button')}
              </Button>
            )}

            {/* Info Text */}
            <Text fontSize={'xs'} color={'ninja.300'} textAlign={'center'} lineHeight={1.5} whiteSpace="pre-line">
              {t('shu-faucet.info')}
            </Text>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
